import React from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';

import { Prose } from '@stratumn/atomic';
import { notify } from '@/components/toast';
import { sig } from '@stratumn/js-crypto';

import { deepGet } from '@/utils';
import { withUser } from '@/contexts';
import { withSpan, SpanType } from '@/tracing';

import { Button } from '@/shadcn/button';
import { TestId } from '@/types/testId';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Input } from '@/shadcn/input';
import { Label } from '@/shadcn/label';
import { KeyRound, LockOpen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider } from '@/shadcn/tooltip';

export class SignLinkDialog extends React.PureComponent {
  static propTypes = {
    user: PropTypes.object.isRequired,
    onSendLink: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    children: PropTypes.node,
    disabled: PropTypes.bool,
    focusElement: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.func
    ])
  };

  static defaultProps = {
    description: null,
    children: null,
    disabled: false,
    focusElement: null
  };

  state = {
    password: undefined,
    wrongPassword: false
  };

  handlePasswordChange = e => {
    this.setState({ password: e.target.value, wrongPassword: false });
  };

  // ==========================================================================
  // Get the user password if the key is protected
  // ==========================================================================

  decryptWallet = () => {
    const {
      user: {
        setPassword,
        me: {
          account: { signingKey }
        }
      }
    } = this.props;
    const { password } = this.state;

    try {
      if (deepGet(signingKey, 'privateKey.passwordProtected', false)) {
        new sig.SigningPrivateKey({
          pemPrivateKey: signingKey.privateKey.decrypted,
          password
        }).export();
      }
    } catch {
      this.setState({ wrongPassword: true });
      return;
    }

    setPassword(password);
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      this.decryptWallet();
    }
  };

  renderPasswordEntry = () => {
    const { wrongPassword, password } = this.state;

    return (
      <>
        <div className="flex flex-col items-center">
          <div className="text-muted-foreground my-6 flex w-full flex-col items-center gap-6 text-sm">
            <KeyRound size={32} />
            <p>Your key is password protected.</p>
          </div>
          <div className="flex w-2/3 flex-col items-start gap-2">
            <Label>Enter your password to decrypt your key</Label>
            <div className="flex w-full items-center gap-1">
              <Input
                className="w-full"
                value={password}
                type="password"
                placeholder="Password"
                invalid={wrongPassword}
                onChange={this.handlePasswordChange}
                onKeyPress={this.handleKeyPress}
              />
              <TooltipProvider>
                <Tooltip>
                  <Button
                    variant="default"
                    size="icon"
                    onClick={this.decryptWallet}
                    data-cy="decrypt-key-button"
                    data-testid={TestId.SignLinkDialog.DecryptButton}
                    aria-label="Submit password"
                  >
                    <LockOpen />
                  </Button>
                  <TooltipContent>Click to submit password</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {wrongPassword && (
              <p
                className="text-destructive text-xs"
                data-cy="wrong-password-message"
              >
                The password you entered is incorrect.
              </p>
            )}
          </div>
        </div>
      </>
    );
  };

  // ==========================================================================
  // Use the key to sign the link
  // ==========================================================================

  getSigningPrivateKey = () => {
    const {
      user: {
        me: {
          account: { signingKey }
        },
        password
      }
    } = this.props;

    const { passwordProtected, decrypted } = signingKey.privateKey;

    if (passwordProtected)
      return new sig.SigningPrivateKey({
        pemPrivateKey: decrypted,
        password
      }).export();

    return new sig.SigningPrivateKey({
      pemPrivateKey: decrypted
    }).export();
  };

  createLink = () =>
    withSpan('signLink', SpanType.crypto, () => {
      const { onSendLink } = this.props;

      let signingKey;
      try {
        signingKey = this.getSigningPrivateKey();
      } catch (err) {
        notify.error(err);
        return;
      }

      onSendLink(signingKey).catch(err => {
        console.error(err);
        notify.error('Something went wrong while creating a link.');
      });
    });

  renderNoKey = () => {
    const { title, onClose, open } = this.props;

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <Prose text="Your private signing key is not available through your Stratumn Account." />
          <Prose text="To perform actions on Trace, please update your account." />
        </DialogContent>
      </Dialog>
    );
  };

  renderSignLink = () => {
    const { onClose, disabled, open, children, user } = this.props;

    if (!open) return null;
    const {
      me: {
        account: { signingKey }
      }
    } = user;

    if (!signingKey || !signingKey.privateKey) return this.renderNoKey();

    return (
      <>
        {children && <Prose text={children} />}
        <DialogFooter className="pt-2">
          <DialogClose asChild>
            <Button
              onClick={onClose}
              variant="outline"
              data-cy="cancel-sign-button"
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={this.createLink}
            disabled={disabled}
            data-cy="sign-link-button"
            data-testid={TestId.SignLinkDialog.SignButton}
          >
            Sign action
          </Button>
        </DialogFooter>
      </>
    );
  };

  render() {
    const { title, onClose, open, user, description } = this.props;

    if (!open) return null;
    const {
      me: {
        account: { signingKey }
      },
      password
    } = user;

    if (!signingKey || !signingKey.privateKey) return this.renderNoKey();

    const passwordPrompt = signingKey.privateKey.passwordProtected && !password;

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {description && <Prose text={description} />}
            </DialogDescription>
          </DialogHeader>
          {passwordPrompt ? this.renderPasswordEntry() : this.renderSignLink()}
        </DialogContent>
      </Dialog>
    );
  }
}

export default compose(withUser)(SignLinkDialog);
